import React, { useState } from 'react';
import classNames from 'classnames';
import {
  canChangePage,
  getVisiblePages,
} from 'src/components/CustomPagination/helpers';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationJumpToBeginning,
  PaginationJumpToEnd,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from 'src/components/CustomPagination/Pagination';
import Skeleton from 'src/components/Skeleton/Skeleton';
import Icon from 'src/components/Icon/FontAwesomeIcon';
import NewBadge from 'src/components/NewBadge/NewBadge';
import ItemsPerPageDialogue from 'src/components/CustomPagination/ItemsPerPageDialogue';
import './custom-pagination.scss';

const SKELETON_PAGE_BUTTON_COUNT: number = 4;

interface CustomPaginationProps {
  // Required.
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;

  // Optional.
  showFirstLast?: boolean;
  disabled?: boolean;
  isLoading?: boolean;
  isSkeleton?: boolean;
  className?: string;

  // Optional items-per-page summary + customization.
  totalItems?: number;
  currentItemsPerPage?: number;
  onItemsPerPageChange?: (next: number) => void;
}

const CustomPagination = ({
  currentPage,
  totalPages,
  onPageChange,
  showFirstLast = true,
  disabled = false,
  isLoading = false,
  isSkeleton = false,
  className,
  totalItems,
  currentItemsPerPage,
  onItemsPerPageChange,
}: CustomPaginationProps) => {
  const [isItemsPerPageDialogueOpen, setIsItemsPerPageDialogueOpen] = useState<boolean>(false);

  const canEditItemsPerPage: boolean =
    onItemsPerPageChange !== undefined && currentItemsPerPage !== undefined;

  const hasItemCountSummary: boolean =
    totalItems !== undefined &&
    currentItemsPerPage !== undefined &&
    totalItems > 0;

  if (isSkeleton) {
    return (
      <CustomPaginationSkeleton
        showFirstLast={showFirstLast}
        showMeta={canEditItemsPerPage || hasItemCountSummary}
        className={className}
      />
    );
  }

  const firstPage: number = 1;
  const prevPage: number = currentPage - 1;
  const nextPage: number = currentPage + 1;
  const finalPage: number = totalPages;

  const handlePageChange = (page: number): void => {
    if (canChangePage(page, currentPage, totalPages, disabled, isLoading)) {
      onPageChange(page);
    }
  };

  const itemsPerPage: number = currentItemsPerPage ?? 0;
  const totalItemCount: number = totalItems ?? 0;
  const previousPageIndex: number = currentPage - 1;

  const itemsBeforeCurrentPage: number = previousPageIndex * itemsPerPage;
  const itemsRemainingOnCurrentPage: number = totalItemCount - itemsBeforeCurrentPage;

  const visibleItemCount: number = Math.max(
    0,
    Math.min(itemsPerPage, itemsRemainingOnCurrentPage)
  );

  const wrapperClassName: string = classNames('cp-footer', className);

  return (
    <div className={wrapperClassName}>
      <Pagination className="cp-wrapper">
        <PaginationContent>
        {/* Jump to first page. */}
        {showFirstLast && (
          <PaginationItem>
            <PaginationJumpToBeginning
              disabled={
                !canChangePage(
                  firstPage,
                  currentPage,
                  totalPages,
                  disabled,
                  isLoading
                )
              }
              onClick={(): void => handlePageChange(firstPage)}
            />
          </PaginationItem>
        )}

        {/* Previous. */}
        <PaginationItem>
          <PaginationPrevious
            disabled={
              !canChangePage(
                prevPage,
                currentPage,
                totalPages,
                disabled,
                isLoading
              )
            }
            onClick={(): void => handlePageChange(prevPage)}
          />
        </PaginationItem>

        {/* Page number window. */}
        {getVisiblePages(totalPages, currentPage).map((page: number) => (
          <PaginationItem key={page}>
            <PaginationLink
              isActive={page === currentPage}
              disabled={disabled || isLoading}
              ariaLabel={`Go to page ${page}`}
              onClick={(): void => handlePageChange(page)}
            >
              {page}
            </PaginationLink>
          </PaginationItem>
        ))}

        {/* Next. */}
        <PaginationItem>
          <PaginationNext
            disabled={
              !canChangePage(
                nextPage,
                currentPage,
                totalPages,
                disabled,
                isLoading
              )
            }
            onClick={(): void => handlePageChange(nextPage)}
          />
        </PaginationItem>

        {/* Jump to last page. */}
        {showFirstLast && (
          <PaginationItem>
            <PaginationJumpToEnd
              disabled={
                !canChangePage(
                  finalPage,
                  currentPage,
                  totalPages,
                  disabled,
                  isLoading
                )
              }
              onClick={(): void => handlePageChange(finalPage)}
            />
          </PaginationItem>
        )}
      </PaginationContent>
      </Pagination>

      {(hasItemCountSummary || canEditItemsPerPage) && (
        <div className="cp-meta">
          {hasItemCountSummary && (
            <span className="cp-summary">
              {`Showing ${visibleItemCount} of ${totalItems} items`}
            </span>
          )}
          {canEditItemsPerPage && (
            <>
              <button
                type="button"
                className="cp-gear"
                aria-label="Change items per page"
                onClick={(): void => setIsItemsPerPageDialogueOpen(true)}
              >
                <Icon glyph="cog" weight="light" />
              </button>
              <NewBadge />
            </>
          )}
        </div>
      )}

      {onItemsPerPageChange !== undefined && currentItemsPerPage !== undefined && (
        <ItemsPerPageDialogue
          isOpen={isItemsPerPageDialogueOpen}
          onClose={(): void => setIsItemsPerPageDialogueOpen(false)}
          currentValue={currentItemsPerPage}
          onSave={onItemsPerPageChange}
        />
      )}
    </div>
  );
};

interface CustomPaginationSkeletonProps {
  showFirstLast: boolean;
  showMeta: boolean;
  className?: string;
}

const CustomPaginationSkeleton = ({
  showFirstLast,
  showMeta,
  className,
}: CustomPaginationSkeletonProps) => {
  const wrapperClassName: string = classNames('cp-footer', className);
  const buttonHeight: string = '2rem';

  return (
    <div className={wrapperClassName}>
      <Pagination className="cp-wrapper">
        <PaginationContent>
          {showFirstLast && (
            <PaginationItem>
              <Skeleton variant="rect" width="2rem" height={buttonHeight} />
            </PaginationItem>
          )}

          <PaginationItem>
            <Skeleton variant="rect" width="6rem" height={buttonHeight} />
          </PaginationItem>

          {Array.from(
            { length: SKELETON_PAGE_BUTTON_COUNT },
            (_, index: number) => (
              <PaginationItem key={`pagination-skeleton-page-${index}`}>
                <Skeleton variant="rect" width="2rem" height={buttonHeight} />
              </PaginationItem>
            )
          )}

          <PaginationItem>
            <Skeleton variant="rect" width="5rem" height={buttonHeight} />
          </PaginationItem>

          {showFirstLast && (
            <PaginationItem>
              <Skeleton variant="rect" width="2rem" height={buttonHeight} />
            </PaginationItem>
          )}
        </PaginationContent>
      </Pagination>

      {showMeta && (
        <div className="cp-meta">
          <Skeleton
            variant="rect"
            width="9rem"
            height="1.3rem"
            className="cp-summary"
          />
          <Skeleton variant="rect" width="3.2rem" height="3.2rem" />
          <Skeleton variant="rect" width="3.5rem" height="1.6rem" />
        </div>
      )}
    </div>
  );
};

export default CustomPagination;
